import React from "react";

import WordButton from "../WordButton";

import styles from "./GameGrid.module.css";

import { useSpring, animated } from "react-spring";
import { PuzzleDataContext } from "../../providers/PuzzleData";
import { GameStatusContext } from "../../providers/GameStatus";

import { Popover, PopoverTrigger } from "../ui/popover";
import { Badge } from "../ui/badge";

// VIBRANT game colors for difficulty levels
const DIFFICULTY_COLOR_MAP = {
    1: { bg: "#FDE047", text: "#713F12" }, // Yellow - Bright, saturated
    2: { bg: "#4ADE80", text: "#14532D" }, // Green - Vibrant
    3: { bg: "#60A5FA", text: "#1E3A8A" }, // Blue - Bright
    4: { bg: "#C084FC", text: "#581C87" }, // Purple - Vibrant
};

function WordRow({ words }) {
    return (
        <div className="grid grid-cols-4 gap-2">
            {words.map((word) => (
                <WordButton key={word} word={word} fullCandidateSize={words.length} />
            ))}
        </div>
    );
}

export function SolvedWordRow({ ...props }) {
    const colors = DIFFICULTY_COLOR_MAP[props.difficulty] || DIFFICULTY_COLOR_MAP[1];

    const [hasBeenClicked, setHasBeenClicked] = React.useState(false);

    const springProps = useSpring({
        from: {
            opacity: 0,
            transform: "translateY(20px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0px)",
        },
        delay: 150,
        config: {
            tension: 300,
            friction: 20,
        },
    });

    // if there is an image available render it as a popover
    const isImageAvailable = props.imageSrc != null;

    const RowContent = () => (
        <>
            <p
                className="text-center font-bold text-sm uppercase tracking-wide"
                style={{ color: colors.text }}
            >
                {props.category}
            </p>
            <p
                className="text-center font-medium text-sm mt-1"
                style={{ color: colors.text }}
            >
                {props.words.join(", ")}
            </p>
        </>
    );

    return (
        <animated.div style={springProps}>
            {!isImageAvailable ? (
                <div
                    className="rounded-lg py-4 px-3"
                    style={{ backgroundColor: colors.bg }}
                >
                    <RowContent />
                </div>
            ) : (
                <Popover>
                    <PopoverTrigger asChild>
                        <div
                            className="cursor-pointer hover:opacity-90 transition-opacity rounded-lg py-4 px-3 relative"
                            style={{ backgroundColor: colors.bg }}
                            onClick={() => setHasBeenClicked(true)}
                        >
                            {!hasBeenClicked && (
                                <Badge className="animate-pulse absolute top-2 right-2 text-xs">
                                    View More
                                </Badge>
                            )}
                            <RowContent />
                        </div>
                    </PopoverTrigger>
                </Popover>
            )}
        </animated.div>
    );
}

function GameGrid({ gameRows, shouldGridShake, setShouldGridShake }) {
    const { submittedGuesses, isGameOver, isGameWon, solvedGameData } =
        React.useContext(GameStatusContext);

    const { gameData } = React.useContext(PuzzleDataContext);

    React.useEffect(() => {
        const shakeEffect = window.setTimeout(() => {
            setShouldGridShake(false);
        }, 600);

        return () => window.clearTimeout(shakeEffect);
    }, [submittedGuesses, setShouldGridShake]);

    const isGameOverAndLost = isGameOver && !isGameWon;
    const isGameOverAndWon = isGameOver && isGameWon;
    const isGameActive = !isGameOver;
    const isGameActiveWithAnySolvedRows =
        isGameActive && solvedGameData.length > 0;

    return (
        <div className="space-y-2">
            {/* Solved rows - show at top during game or when won */}
            {(isGameOverAndWon || isGameActiveWithAnySolvedRows) && (
                <div className="space-y-2 mb-2">
                    {solvedGameData.map((solvedRowObj) => (
                        <SolvedWordRow key={solvedRowObj.category} {...solvedRowObj} />
                    ))}
                </div>
            )}

            {/* Active game grid */}
            {isGameActive && (
                <div className={`space-y-2 ${shouldGridShake ? styles.shake : ""}`}>
                    {gameRows.map((row, idx) => (
                        <WordRow key={idx} words={row} />
                    ))}
                </div>
            )}

            {/* Show correct answers if lost */}
            {isGameOverAndLost && (
                <div className="space-y-2">
                    <p className="text-center text-sm text-gray-500 mb-3">
                        The correct categories were:
                    </p>
                    {gameData.map((obj) => (
                        <SolvedWordRow key={obj.category} {...obj} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default GameGrid;
